import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import "./Upload.css";
import { BASE_URL } from "./Constants";
import useAuth from './useAuth';
import * as XLSX from "xlsx"; // Import the xlsx library

const Viva = () => {
  const [viva, setViva] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDept, setSelectedDept] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [user, setUser] = useState(null);

  useAuth();

  useEffect(() => {
    const token = localStorage.getItem("authToken");

    const fetchProfile = async () => {
      try {
        const response = await fetch(`${BASE_URL}profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch profile");
        }

        const data = await response.json();
        console.log("Fetched Profile Data:", data);
        setUser(data);
        setEmpCode(data?.profile?.emp_code);
        return data;
      } catch (err) {
        setError(err.message);
        return null;
      }
    };

    const fetchViva = async (profile) => {
      try {
        const isAdmin = profile?.profile?.is_admin === "Y";
        const url =  `${BASE_URL}viva?ses=${encodeURIComponent(profile?.profile?.emp_code)}&isAdmin=${encodeURIComponent(isAdmin)}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch viva data");
        }

        const data = await response.json();
        console.log("Fetched Viva Data:", data);
        setViva(data.viva || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const init = async () => {
      setLoading(true);
      const profile = await fetchProfile();
      if (profile) {
        await fetchViva(profile);
      }
    };

    init();
  }, []);

  useEffect(() => {
    let filtered = viva;
  
    if (searchQuery) {
      filtered = filtered.filter((q) => {
        // Ensure fields are strings before calling toLowerCase
        const empId = q.emp_id ? q.emp_id.toLowerCase() : '';
        const deptName = q.dept_name ? q.dept_name.toLowerCase() : '';
        const createdBy = q.created_by ? q.created_by.toLowerCase() : '';
        const compName = q.comp_name ? q.comp_name.toLowerCase() : '';
        const writtenMarks = q.written_marks ? q.written_marks.toString().toLowerCase() : ''; // Convert to string to handle numbers
        const vivaMarks = q.viva_total_marks ? q.viva_total_marks.toString().toLowerCase() : ''; // Convert to string to handle numbers
  
        // Return true if any field matches the search query
        return (
          empId.includes(searchQuery.toLowerCase()) ||
          deptName.includes(searchQuery.toLowerCase()) ||
          createdBy.includes(searchQuery.toLowerCase()) ||
          compName.includes(searchQuery.toLowerCase()) ||
          writtenMarks.includes(searchQuery.toLowerCase()) ||
          vivaMarks.includes(searchQuery.toLowerCase())
        );
      });
    }
  
    if (selectedDept) {
      filtered = filtered.filter((q) => q.dept_name === selectedDept);
    }
  
    setFilteredQuestions(filtered);
  }, [searchQuery, selectedDept, viva]);

  const paginateQuestions = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredQuestions.slice(startIndex, endIndex);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleDeptChange = (e) => {
    setSelectedDept(e.target.value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Export to Excel functionality
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredQuestions);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Viva Data");
    XLSX.writeFile(wb, "viva_data.xlsx");
  };

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper" >
        <Sidebar />

        <div className="main-panel">
          <div className="content-wrapper">
            <div className="row">
              <div className="col-lg-12 grid-margin stretch-card">
                <div className="card">
                  <div className="card-body">
                    <h4 className="card-title">Viva Completed List</h4>

                    <div className="mb-3">
                      <div className="row">
                        <div className="col-lg-1">
                          <select
                            className="form-control input-font"
                            value={itemsPerPage}
                            style={{padding:"0px 10px"}}
                            onChange={handleRowsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                        <div className="col-lg-5">
                          <select
                            className="form-control input-font"
                            value={selectedDept}
                            style={{padding:"0px 10px"}}
                            onChange={handleDeptChange}
                          >
                            <option value="">All Departments</option>
                            {Array.from(new Set(viva.map((q) => q.dept_name))).map((dept_name) => (
                              <option key={dept_name} value={dept_name}>
                                {dept_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-lg-5">
                          <input
                            type="text"
                            className="form-control input-font"
                            placeholder="Search questions"
                            value={searchQuery}
                            onChange={handleSearchChange}
                          />
                        </div>
                        <div className="col-lg-1">
                          <button
                          className="btn btn-success mb-3 input-font"
                          style={{padding:"5px 10px"}}
                          onClick={exportToExcel}
                        >
                          Export to Excel
                        </button>
                        </div>
                      </div>
                    </div>

                    {loading && <p>Loading...</p>}
                    {error && <p style={{ color: "red" }}>{error}</p>}

                    {!loading && !error && filteredQuestions.length > 0 && (
                      <>
                        {/* <button
                          className="btn btn-success mb-3"
                          onClick={exportToExcel}
                        >
                          Export to Excel
                        </button> */}

                        <table className="table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Date</th>
                              <th>Employee ID</th>
                              <th>Name</th>
                              <th>Department</th>
                              <th>Written Exam Marks</th>
                              <th>Viva Marks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginateQuestions().map((questionItem, index) => (
                              <tr key={questionItem.id}>
                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                <td>{questionItem.created_on}</td>
                                <td>{questionItem.emp_id}</td>
                                <td>{questionItem.comp_name || "N/A"}</td>
                                <td>{questionItem.dept_name}</td>
                                <td>{questionItem.written_marks}</td>
                                <td>{questionItem.viva_total_marks}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
<br/>
                        <div className="pagination">
                          <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="btn btn-secondary"
                          >
                            Previous
                          </button>
                          <span className="mx-3">Page {currentPage}</span>
                          <button
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, Math.ceil(filteredQuestions.length / itemsPerPage))
                              )
                            }
                            disabled={currentPage === Math.ceil(filteredQuestions.length / itemsPerPage)}
                            className="btn btn-secondary"
                          >
                            Next
                          </button>
                        </div>

                        <div className="row-count-display">
                          <p>
                            Showing {(currentPage - 1) * itemsPerPage + 1}-
                            {Math.min(currentPage * itemsPerPage, filteredQuestions.length)} of{" "}
                            {filteredQuestions.length} rows
                          </p>
                        </div>
                      </>
                    )}

                    {!loading && !error && filteredQuestions.length === 0 && <p>No questions found</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <footer className="footer">
            <span className="text-muted">© {new Date().getFullYear()} Vijaya Diagnostic Centre</span>
          </footer>
        </div>
      </div>

     
    </div>
  );
};

export default Viva;
